import { computed, unref } from 'vue';
import type { Ref } from 'vue';
import { useFormBuilderStore } from './form-builder.store';
import { useTableEditorStore } from './table-editor.store';
import type { FormElement } from './form.types';

interface UpdateElementOptions {
  trackHistory?: boolean;
}

export function useElementProperties(
  elementId: (() => string | undefined) | Ref<string | undefined> | string | undefined
) {
  const formBuilderStore = useFormBuilderStore();
  const tableEditorStore = useTableEditorStore();

  const selectedElement = computed(() => {
    const id = typeof elementId === 'function' ? elementId() : unref(elementId);

    if (id) {
      const formElement = formBuilderStore.elements.find((el) => el.id === id);
      if (formElement) return formElement;

      const position = tableEditorStore.findCellByElementId(id);
      if (position && tableEditorStore.activeTable) {
        const cell = tableEditorStore.activeTable.properties.cells[position.row][position.col];
        return cell.content || null;
      }

      return null;
    }

    return formBuilderStore.selectedElement;
  });

  const cellPosition = computed(() => {
    const id = typeof elementId === 'function' ? elementId() : unref(elementId);
    if (!id) return null;
    return tableEditorStore.findCellByElementId(id);
  });

  function updateElement(updates: Partial<FormElement>, options?: UpdateElementOptions) {
    if (!selectedElement.value) return;

    const position = cellPosition.value;

    if (position) {
      tableEditorStore.updateCellContent(position.row, position.col, updates, options);
    } else {
      formBuilderStore.updateElement(selectedElement.value.id, updates, options);
    }
  }

  return {
    selectedElement,
    cellPosition,
    updateElement,
  };
}
